import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';

import { makeStyles } from '@toolbox/style';

import type { FC } from 'react';

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${theme.spacing(4)};
    padding-top: ${theme.spacing(4)};
    padding-bottom: ${theme.spacing(4)};

    ${theme.media.up('md')} {
      padding-top: ${theme.spacing(6)};
      padding-bottom: ${theme.spacing(8)};
    }
  `,
  time: css`
    background-color: transparent;
    appearance: none;
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.palette.colors.gray[400]};
    color: ${theme.palette.colors.primary};
    text-align: center;

    &[data-active='true'],
    &:hover,
    &:focus {
      background-color: ${theme.palette.colors.petrol[100]};
      border-color: currentColor;
      cursor: pointer;
    }
  `,
}));

type TimePickerProps = {
  selectedIndex: number;
  times: string[];
  onTimeChange: (time: number) => void;
};

export const TimePicker: FC<TimePickerProps> = ({ selectedIndex, times, onTimeChange }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(selectedIndex);

  const updateIndex = (jump: number): void => {
    const lastIndex = times.length - 1;
    const targetIndex = active + jump;

    if (targetIndex > lastIndex) {
      setActive(targetIndex - lastIndex - 1);
    } else if (targetIndex < 0) {
      setActive(lastIndex + targetIndex + 1);
    } else {
      setActive(targetIndex);
    }
    onTimeChange(-1);
  };

  const updateTime = (index: number): void => {
    setActive(index);
    onTimeChange(index);
  };

  useEffect(() => {
    const onKeyUp = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'ArrowUp':
          updateIndex(-3);
          break;
        case 'ArrowDown':
          updateIndex(3);
          break;
        case 'ArrowLeft':
          updateIndex(-1);
          break;
        case 'ArrowRight':
          updateIndex(1);
          break;
        case 'Enter':
          updateTime(active);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (rootRef.current) {
      const selectedChild = rootRef.current.querySelector('[data-active="true"]') as HTMLElement;
      selectedChild && selectedChild.focus();
    }
  }, [active]);

  const classes = useStyles();
  return (
    <div css={classes.root as any} role="grid" ref={rootRef} data-testid="timepicker">
      {times.map((time, index) => {
        const isSelected = index === selectedIndex;
        const isActive = index === active;
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div
            key={index}
            role="gridcell"
            css={classes.time as any}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={isSelected ? 0 : -1}
            data-active={isActive}
            aria-selected={isSelected}
            onClick={() => updateTime(index)}
          >
            {time}
          </div>
        );
      })}
    </div>
  );
};
